import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import PhygitalIcon from '@/components/PhygitalIcon';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  Settings,
  Edit3,
  Save,
  X,
  RefreshCw,
  Eye,
  Search,
  ChevronRight,
  Shield,
  Camera,
  Trash2,
  Bell,
  Heart,
  Utensils,
  Accessibility,
  Sparkles,
  Clock,
  Building,
  Tag,
  ExternalLink,
  Check,
  AlertTriangle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MirroredProfileEditor
 * 
 * Admin page that mirrors the UserProfilePage exactly for direct editing.
 * Allows admins to edit any user's profile while seeing exactly how it looks.
 */
export default function MirroredProfileEditor() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('user');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserSearch, setShowUserSearch] = useState(!userId);
  
  // Editable user fields
  const [editedUser, setEditedUser] = useState({});
  
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  
  useEffect(() => {
    loadUsers();
    if (userId) {
      loadUserProfile(userId);
    }
  }, [userId]);
  
  const loadUsers = async () => {
    try {
      const res = await fetch(`${API}/admin/database/users`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        const usersList = Array.isArray(data) ? data : (data.records || []);
        setUsers(usersList);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };
  
  const loadUserProfile = async (uid) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/users/${uid}/profile`, { headers: getAuthHeaders() });
      if (res.ok) {
        const user = await res.json();
        setSelectedUser(user);
        setEditedUser(user);
        setShowUserSearch(false);
      } else {
        // Fallback to searching in users list
        const foundUser = users.find(u => u.user_id === uid);
        if (foundUser) {
          setSelectedUser(foundUser);
          setEditedUser(foundUser);
          setShowUserSearch(false);
        }
      }
    } catch (err) {
      console.error('Error loading user profile:', err);
      toast.error('Failed to load user profile');
    }
    setLoading(false);
  };
  
  const selectUser = (user) => {
    setSelectedUser(user);
    setEditedUser(user);
    setShowUserSearch(false);
    navigate(`/admin/profile-editor?user=${user.user_id}`);
  };
  
  const saveUserProfile = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/users/${selectedUser.user_id}/profile`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editedUser)
      });
      
      if (res.ok) {
        toast.success('Profile saved successfully');
        setSelectedUser(editedUser);
        setEditMode(false);
      } else {
        toast.error('Failed to save profile');
      }
    } catch (err) {
      toast.error('Failed to save profile');
    }
    setSaving(false);
  };
  
  const updateField = (field, value) => {
    setEditedUser(prev => ({ ...prev, [field]: value }));
  };
  
  // Filter users for search
  const filteredUsers = users.filter(u => {
    const query = searchQuery.toLowerCase();
    return (
      (u.first_name || '').toLowerCase().includes(query) ||
      (u.last_name || '').toLowerCase().includes(query) ||
      (u.email || '').toLowerCase().includes(query)
    );
  });
  
  // Editable field component - shows value or input based on edit mode
  const EditableField = ({ label, field, type = 'text', icon: Icon, options = null }) => {
    const value = editedUser[field] || '';
    
    return (
      <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50">
        {Icon && <Icon className="w-5 h-5 text-gray-400 mt-0.5" />}
        <div className="flex-1">
          <Label className="text-xs text-gray-500">{label}</Label>
          {editMode ? (
            options ? (
              <Select value={value} onValueChange={(val) => updateField(field, val)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={`Select ${label}...`} />
                </SelectTrigger>
                <SelectContent>
                  {options.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : type === 'textarea' ? (
              <Textarea 
                value={value}
                onChange={(e) => updateField(field, e.target.value)}
                className="mt-1"
                rows={3}
              />
            ) : (
              <Input 
                type={type}
                value={value}
                onChange={(e) => updateField(field, e.target.value)}
                className="mt-1"
              />
            )
          ) : (
            <p className="text-sm font-medium mt-1">{value || <span className="text-gray-400 italic">Not set</span>}</p>
          )}
        </div>
      </div>
    );
  };
  
  if (loading && userId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="mirrored-profile-editor">
      {/* Admin Control Bar */}
      <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/people')} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to People
            </Button>
            <h1 className="text-lg font-semibold">Profile Editor</h1>
            <Badge variant="outline" className="text-purple-600">ADMIN MODE</Badge>
          </div>
          
          <div className="flex items-center gap-3">
            {selectedUser && (
              <>
                <Button variant="outline" onClick={() => setEditMode(!editMode)}>
                  {editMode ? <Eye className="w-4 h-4 mr-2" /> : <Edit3 className="w-4 h-4 mr-2" />}
                  {editMode ? 'Preview' : 'Edit'}
                </Button>
                
                {editMode && (
                  <Button onClick={saveUserProfile} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                )}
                
                <Button variant="outline" onClick={() => setShowUserSearch(true)}>
                  Switch User
                </Button>
              </>
            )}
          </div>
        </div>
        
        {editMode && (
          <div className="bg-purple-50 border-t border-purple-200 px-4 py-2">
            <p className="text-sm text-purple-700">
              <Edit3 className="w-4 h-4 inline mr-2" />
              Editing profile for <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong> ({selectedUser?.email})
            </p>
          </div>
        )}
      </div>
      
      {/* User Search Dialog */}
      <Dialog open={showUserSearch} onOpenChange={setShowUserSearch}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Select User to Edit</DialogTitle>
            <DialogDescription>Search for a user whose profile you want to edit</DialogDescription>
          </DialogHeader>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          
          <ScrollArea className="h-80">
            <div className="space-y-2">
              {filteredUsers.slice(0, 30).map((user) => (
                <div
                  key={user.user_id || user._id}
                  className="flex items-center gap-3 p-3 rounded-lg border hover:border-purple-300 hover:bg-purple-50 cursor-pointer transition-colors"
                  onClick={() => selectUser(user)}
                >
                  {user.profile_image ? (
                    <img src={user.profile_image} alt="" className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-medium">
                      {(user.first_name || user.email || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{user.first_name} {user.last_name}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
      
      {/* MIRRORED /me PAGE - Exact replica */}
      {selectedUser ? (
        <div className="max-w-5xl mx-auto p-6">
          {/* Header - Like the actual /me page */}
          <header className="bg-white border-b border-gray-200 rounded-t-xl mb-0">
            <div className="px-4 py-4 flex items-center justify-between">
              <Link to="/home" className="flex items-center gap-2">
                <PhygitalIcon className="w-8 h-8" />
                <span className="font-semibold text-lg">phygital</span>
              </Link>
              <div className="flex items-center gap-3">
                <Badge variant="outline">Synced</Badge>
                <Button variant="ghost" size="sm" className="relative">
                  <Bell className="h-5 w-5" />
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">9+</span>
                </Button>
                <Button variant="ghost" size="sm">
                  <Settings className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </header>
          
          {/* Tabs - Like /me page */}
          <div className="bg-white rounded-b-xl shadow-sm mb-6">
            <Tabs defaultValue="dashboard">
              <TabsList className="w-full justify-start border-b rounded-none px-4">
                <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                <TabsTrigger value="defaults">Defaults</TabsTrigger>
                <TabsTrigger value="inspo">Inspo</TabsTrigger>
                <TabsTrigger value="experiences">Experiences</TabsTrigger>
              </TabsList>
              
              <TabsContent value="dashboard" className="p-4">
                {/* Profile Card */}
                <Card className="mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      {/* Profile Image */}
                      <div className="relative">
                        {editedUser.profile_image ? (
                          <img 
                            src={editedUser.profile_image} 
                            alt="" 
                            className="h-20 w-20 rounded-full object-cover border-4 border-white shadow-lg"
                          />
                        ) : (
                          <div className="h-20 w-20 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                            {(editedUser.first_name || editedUser.email || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        {editMode && (
                          <button className="absolute bottom-0 right-0 bg-purple-600 text-white rounded-full p-1.5 shadow-lg">
                            <Camera className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Profile Info */}
                      <div className="flex-1">
                        {editMode ? (
                          <div className="grid grid-cols-2 gap-2">
                            <Input 
                              value={editedUser.first_name || ''} 
                              onChange={(e) => updateField('first_name', e.target.value)}
                              placeholder="First Name"
                            />
                            <Input 
                              value={editedUser.last_name || ''} 
                              onChange={(e) => updateField('last_name', e.target.value)}
                              placeholder="Last Name"
                            />
                          </div>
                        ) : (
                          <h2 className="text-xl font-bold">{editedUser.first_name} {editedUser.last_name}</h2>
                        )}
                        <p className="text-sm text-gray-500 mt-1 font-mono">
                          {editedUser.share_id || editedUser.user_id?.slice(-8).toUpperCase()}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline">{editedUser.role || 'User'}</Badge>
                          {editedUser.is_verified && <Badge className="bg-green-100 text-green-700"><Check className="w-3 h-3 mr-1" />Verified</Badge>}
                        </div>
                      </div>
                      
                      {/* Share Button */}
                      <Button variant="outline" size="sm">
                        <ExternalLink className="w-4 h-4 mr-2" /> Share
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Contact & Basic Info */}
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-base">Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    <EditableField label="Email" field="email" type="email" icon={Mail} />
                    <EditableField label="Phone" field="phone" type="tel" icon={Phone} />
                    <EditableField label="Birthday" field="birthday" type="date" icon={Calendar} />
                    <EditableField label="Location" field="location" icon={Building} />
                  </CardContent>
                </Card>
                
                {/* Tags */}
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Tag className="w-4 h-4" /> Tags
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {(editedUser.tags || []).map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                          {tag}
                          {editMode && (
                            <X 
                              className="w-3 h-3 cursor-pointer hover:text-red-500" 
                              onClick={() => updateField('tags', editedUser.tags.filter((_, i) => i !== idx))}
                            />
                          )}
                        </Badge>
                      ))}
                      {editMode && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-6"
                          onClick={() => {
                            const newTag = prompt('Enter new tag:');
                            if (newTag) updateField('tags', [...(editedUser.tags || []), newTag]);
                          }}
                        >
                          + Add Tag
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Preferences Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Heart className="w-4 h-4" /> Preferences
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-orange-50 rounded-lg">
                        <div className="flex items-center gap-2 text-orange-700">
                          <Utensils className="w-4 h-4" />
                          <span className="font-medium text-sm">Dietary</span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {editedUser.dietary_restrictions?.length || 0} restrictions
                        </p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-2 text-blue-700">
                          <Accessibility className="w-4 h-4" />
                          <span className="font-medium text-sm">Accessibility</span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {editedUser.accessibility_needs?.length || 0} needs
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="defaults" className="p-4">
                <Card>
                  <CardContent className="py-12 text-center text-gray-500">
                    <Settings className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Defaults configuration - matches user's saved preferences</p>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="inspo" className="p-4">
                <Card>
                  <CardContent className="py-12 text-center text-gray-500">
                    <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Inspiration board - user's saved inspirations</p>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="experiences" className="p-4">
                <Card>
                  <CardContent className="py-12 text-center text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Experiences - user's events and bookings</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="max-w-md w-full">
            <CardContent className="py-12 text-center">
              <User className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium mb-2">Select a User to Edit</h3>
              <p className="text-gray-500 mb-4">Choose a user from the database to view and edit their profile</p>
              <Button onClick={() => setShowUserSearch(true)}>
                <Search className="w-4 h-4 mr-2" /> Search Users
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
